import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { MaskedLines, FadeUp, SectionHeading } from "@/components/Reveal";
import TiltCard from "@/components/TiltCard";
import useSeo from "@/hooks/useSeo";
import { SERVICES } from "@/data/services";
import { SECTORS } from "@/data/content";

const PROCESS = [
  { n: "01", title: "Survey & Sampark", hindi: "सर्वे", desc: "Constituency or market research, voter-list analytics and ground surveys until the real issues show themselves." },
  { n: "02", title: "Narrative", hindi: "संदेश", desc: "One sharp message in the audience's language — then the manifesto, slogans, geet and creatives hang on it." },
  { n: "03", title: "War Room", hindi: "वॉर रूम", desc: "Daily cadence across digital, media and ground. Rapid response within the hour, every hour." },
  { n: "04", title: "Result Day", hindi: "परिणाम", desc: "Booth teams, turnout drives and counting-day coverage — or launch day and the first thousand leads. We stay till the certificate." },
];

const ELECTIONS = ["Lok Sabha", "Vidhan Sabha", "Nagar Nigam", "Nagar Palika", "Zila Panchayat", "Janpad & Gram Panchayat", "Co-operative & University Polls"];

function StackCard({ s, i, total, progress }) {
  const target = 1 - (total - 1 - i) * 0.045;
  const scale = useTransform(progress, [i / total, 1], [1, target]);
  return (
    <motion.article
      style={{ scale, top: `calc(10vh + ${i * 18}px)` }}
      className="sticky mb-10 origin-top"
      data-testid={`service-pillar-${s.n}`}
    >
      <div
        className="relative grid min-h-[72vh] grid-cols-1 gap-10 overflow-hidden rounded-[2rem] border border-white/10 bg-coal p-8 lg:grid-cols-12 lg:p-14"
        style={{ boxShadow: `0 -20px 80px -40px ${s.color}66, inset 0 1px 0 rgba(255,255,255,0.06)` }}
      >
        <div className="pointer-events-none absolute -right-20 -top-20 h-72 w-72 rounded-full blur-3xl" style={{ background: `${s.color}33` }} />
        <div className="flex flex-col justify-between lg:col-span-7">
          <div>
            <div className="flex items-center gap-4">
              <span className="font-display text-5xl font-bold tracking-tight sm:text-7xl" style={{ color: s.color }}>{s.n}</span>
              <span className="font-hindi text-2xl text-paper/80 sm:text-3xl">{s.hindi}</span>
            </div>
            <h2 className="mt-6 font-display text-4xl font-bold uppercase leading-[0.95] tracking-tight text-paper sm:text-6xl lg:text-7xl">{s.title}</h2>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-zinc-400 sm:text-lg">{s.desc}</p>
          </div>
          <div className="mt-10 flex flex-wrap gap-2">
            {s.tags.map((t) => (
              <span key={t} className="rounded-full border px-4 py-1.5 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-300" style={{ borderColor: `${s.color}55` }}>
                {t}
              </span>
            ))}
          </div>
        </div>
        <div className="relative lg:col-span-5">
          <div className="relative h-64 overflow-hidden rounded-2xl lg:h-full">
            <img src={s.img} alt={s.title} className="h-full w-full object-cover grayscale transition-transform duration-700 hover:scale-105" loading="lazy" />
            <div className="absolute inset-0 mix-blend-color" style={{ background: s.color, opacity: 0.55 }} />
            <div className="absolute inset-0 bg-gradient-to-t from-coal via-transparent to-transparent" />
            <Link
              to={`/contact?service=${encodeURIComponent(s.title)}`}
              data-testid={`service-cta-${s.slug}`}
              className="absolute bottom-5 left-5 inline-flex items-center gap-2 rounded-full bg-ink/80 px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] text-paper backdrop-blur-md transition-colors duration-300 hover:bg-paper hover:text-ink"
            >
              Start with {s.title} <ArrowUpRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </div>
      </div>
    </motion.article>
  );
}

export default function Services() {
  useSeo("Services", "Election campaigns, branding, PR, advertising, web & tech and creatives — six disciplines, one war room, from Indore.");
  const stackRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: stackRef, offset: ["start start", "end end"] });

  return (
    <div data-testid="services-page" className="mx-auto max-w-[1440px] px-6 pb-24 pt-36 lg:px-12 lg:pt-44">
      <p className="font-mono text-xs font-medium uppercase tracking-[0.25em] text-saffron">Capabilities — छह विभाग</p>
      <h1 className="mt-5 font-display text-4xl font-bold uppercase leading-[0.95] tracking-tight text-paper sm:text-6xl lg:text-7xl">
        <MaskedLines inView={false} delay={0.1} lines={["Six disciplines.", <span key="w" className="ribbon-text">One war room.</span>]} />
      </h1>
      <FadeUp delay={0.3}>
        <p className="mt-8 max-w-xl text-base leading-relaxed text-zinc-400 sm:text-lg">
          Election campaigns are our core. Around them sit branding, PR, advertising, web and a daily creative
          studio — the same team, the same calendar, for leaders, governments and brands across India.
        </p>
      </FadeUp>
      <FadeUp delay={0.4}>
        <div className="mt-10 flex flex-wrap gap-2" data-testid="services-elections-list">
          {ELECTIONS.map((e) => (
            <span key={e} className="rounded-full border border-saffron/30 bg-saffron/5 px-4 py-1.5 font-mono text-[10px] uppercase tracking-[0.15em] text-saffron">{e}</span>
          ))}
        </div>
      </FadeUp>

      <div ref={stackRef} className="relative mt-20 lg:mt-28" data-testid="services-stack">
        {SERVICES.map((s, i) => (
          <StackCard key={s.slug} s={s} i={i} total={SERVICES.length} progress={scrollYProgress} />
        ))}
      </div>

      <div className="mt-24 lg:mt-32">
        <SectionHeading overline="Sectors — किनके लिए" lines={["Who we", "work for."]} testId="services-sectors-heading" />
        <div className="mt-12 grid grid-cols-2 gap-px overflow-hidden rounded-2xl border border-white/10 bg-white/10 sm:grid-cols-3" data-testid="sectors-grid">
          {SECTORS.map((s, i) => (
            <FadeUp key={s} delay={i * 0.04}>
              <div className="group flex h-full items-center justify-between gap-3 bg-ink px-6 py-6 transition-colors duration-300 hover:bg-coal">
                <p className="font-display text-base font-bold uppercase tracking-tight text-paper transition-transform duration-300 group-hover:translate-x-1">{s}</p>
                <span className="h-2 w-2 rounded-full bg-white/15 transition-colors duration-300 group-hover:bg-saffron" />
              </div>
            </FadeUp>
          ))}
        </div>
      </div>

      <div className="mt-24 lg:mt-32">
        <SectionHeading overline="Process" lines={["From survey", "to certificate."]} testId="services-process-heading" />
        <div className="mt-14 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {PROCESS.map((s, i) => (
            <FadeUp key={s.n} delay={i * 0.08}>
              <TiltCard testId={`process-step-${s.n}`}>
                <div className="group relative h-full rounded-xl border border-white/10 bg-coal/60 p-8 transition-colors duration-500 hover:border-saffron/40">
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-sm text-brand">{s.n}</span>
                    <span className="font-hindi text-lg text-saffron">{s.hindi}</span>
                  </div>
                  <h3 className="mt-6 font-display text-xl font-bold uppercase tracking-tight text-paper">{s.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-zinc-500">{s.desc}</p>
                </div>
              </TiltCard>
            </FadeUp>
          ))}
        </div>
      </div>

      <FadeUp className="mt-24 lg:mt-32">
        <div className="relative overflow-hidden rounded-2xl border border-brand/25 bg-gradient-to-br from-ocean/15 via-coal to-brand/10 p-10 lg:p-16">
          <div className="flex flex-wrap items-center justify-between gap-8">
            <div>
              <p className="font-mono text-xs uppercase tracking-[0.25em] text-saffron">Engagements</p>
              <h2 className="mt-4 font-display text-2xl font-bold uppercase tracking-tight text-paper sm:text-3xl">Campaign retainers from ₹1.5 Lakh/month. Full election packages on request.</h2>
              <p className="mt-3 max-w-lg text-sm text-zinc-400">Tell us the seat, the date and the challenge. We'll tell you honestly what it takes to win.</p>
            </div>
            <Link to="/packages" data-testid="services-cta-button" className="btn-ribbon group inline-flex items-center gap-2 rounded-full px-8 py-4 font-mono text-xs font-medium uppercase tracking-[0.15em] text-white">
              See packages <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </div>
        </div>
      </FadeUp>
    </div>
  );
}
